import { FC, Fragment, memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useBeforeUnload } from 'react-router-dom';
import { CDialog } from '../Dialogs/CDialog';
import { ServiceFactory } from '../../EnterpriseGPTApi/ServiceFactory';
import { EnterpriseGPTApiV1ClientWrapper } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1ClientWrapper';
import type { ISurvey, IQuestion as IApiQuestion, SurveyAnswerType } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { Survey, SurveyStatus } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { SurveyHeader } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../Contexts/NotificationContext';
import { useTheme } from '../../store/hooks';
import { DARK_THEME } from '../../store/models/theme';
import { makeStyles, shorthands, tokens, Button, Text, Input, Textarea, Select, Switch, Divider, mergeClasses } from '@fluentui/react-components';
import { ChevronsUp, ChevronsDown, ChevronUp, ChevronDown } from 'lucide-react';
import { Add24Regular, MoreVertical16Regular, Delete24Regular, ArrowUp24Regular, ArrowDown24Regular, Dismiss24Regular, Save24Regular, ChevronDown24Regular, ChevronUp24Regular } from '@fluentui/react-icons';
import SyButton from '../Common/SyButton';
import {
    Trash2,
    PlusCircle,
    X as LucideX,
} from 'lucide-react';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import '../../global-header.css';
import './SurveyForm.css';

// Path to star icon for Rating preview
const STAR_ICON_PATH = '/icons/star.svg';

// Question types aligned with VisualSurveyEditor-style options
type QuestionType = 'Rating' | 'SingleValue' | 'MultipleValue' | 'Text' | 'NPSScale';
interface Question {
    id: string;
    label: string;
    type: QuestionType;
    required: boolean;
    answers?: string[]; // used for SingleValue and MultipleValue
    maxLength?: number; // used for Text
    maxAnswers?: number; // used for MultipleValue
}

const useStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '0px 0px 24px',
        boxSizing: 'border-box',
        /* page no longer provides the body background so header can be full-bleed */
        background: 'transparent',
        /* ensure the background covers the full viewport width when embedded */
        minHeight: '100%',
    },
    /* Body area containing the content below the main divider. This gets the bordered background. */
    body: {
        background: 'var(--border-bg)',
        padding: '0px 0px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
    },
    contentInner: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        marginTop: '12px',
        padding: '0 24px',
    },
    card: {
        background: 'var(--colorCardBackground1)',
        borderRadius: '8px',
        padding: '20px',
        boxSizing: 'border-box',
        maxWidth: '800px',
        minWidth: '600px',
        margin: '0 auto',
        '@media (min-width: 768px)': {
            minWidth: '700px',
        },
    },
    cardSurveyDetails: {
        background: 'var(--colorCardBackground1)',
        borderRadius: '8px',
        padding: '20px',
        boxSizing: 'border-box',
        maxWidth: '800px',
        minWidth: '600px',
        margin: '24px auto 0',
        '@media (min-width: 768px)': {
            minWidth: '700px',
        },
    },
    surveyDetailsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    surveyDetailsRow: {
        display: 'flex',
        gap: '16px',
    },
    surveyDetailsField: {
        flex: 1,
    },
    fullWidthInput: {
        width: '100%',
    },
    label: {
        color: tokens.colorNeutralForeground1,
        fontSize: '0.75rem',
        lineHeight: '20px',
        fontWeight: tokens.fontWeightSemibold,
        display: 'block',
        marginBottom: '4px',
    },
    requiredAsterisk: {
        color: 'var(--color-red-500)',
    },
    errorText: {
        color: 'var(--color-red-500)',
        fontSize: '12px',
    },
    validationError: {
        color: 'var(--color-red-500)',
        fontSize: '12px',
        marginTop: '6px',
    },
    answersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginTop: '2px',
    },
    addAnswerBtn: {
        marginTop: '8px',
    },
    starsRow: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        marginBottom: '16px',
        marginTop: '6px',
    },
    addQuestionBar: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '18px 24px',
    },
    separator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0',
        width: '100%',
        boxSizing: 'border-box',
        padding: '0 20px', // inset the line to match card padding
    },
    separatorLine: {
        height: '1px',
        flex: 1,
        background: 'var(--color-neutral-200)', // light gray line (from design tokens)
    },
    separatorDot: {
        position: 'relative',
        background: 'transparent',
        border: 'none',
        borderRadius: '50%',
        width: 'auto',
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        margin: '0 0px', // tightened spacing left/right of the icon
        zIndex: 1,
    },
    separatorButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0px',
        margin: 0,
    },
    combinedBadge: {
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: tokens.fontWeightRegular,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '3px 8px 5px 8px',
        marginTop: '2px',
        borderRadius: '6px',
        background: 'transparent',
        boxSizing: 'border-box',
        color: tokens.colorNeutralForeground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.05)',
    },
    combinedLabel: {
        marginLeft: '4px',
    },
    questionInput: {
        fontSize: '0.75rem',
    },
    answerInput: {
        fontSize: '0.75rem',
    },
    answersContainer: {
        background: 'var(--colorNeutralBackground3)',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '10px',
    },
    controlCard: {
        background: 'var(--colorCardBackground1)',
        borderRadius: '8px',
        padding: '12px 20px',
        boxSizing: 'border-box',
        maxWidth: '800px',
        minWidth: '600px',
        margin: '0 auto',
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        '@media (min-width: 768px)': {
            minWidth: '700px',
        },
    },
    customInput: {
        borderRadius: '0.5rem',
        background: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'none',
        '&::after': {
            display: 'none',
        },
        '&:focus-within': {
            outline: '2px solid var(--color-primary-400)',
            outlineOffset: '2px',
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            background: tokens.colorNeutralBackground1,
        },
        '&:focus-within::after': {
            display: 'none',
        }
    },
    customTextarea: {
        borderRadius: '0.5rem',
        background: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'none',
        '&::after': {
            display: 'none',
        },
        '&:focus-within': {
            outline: '2px solid var(--color-primary-400)',
            outlineOffset: '2px',
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            background: tokens.colorNeutralBackground1,
        },
        '&:focus-within::after': {
            display: 'none',
        }
    },
    smallButton: {
        fontSize: '0.75rem',
    },
    titleContainer: {
        flex: 1,
        minWidth: 0,
        maxWidth: '800px',
    },
    titleText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

});

const defaultQuestion = (id: string): Question => ({ id, label: '', type: 'SingleValue', required: false, answers: [''] });

// Memoized small dot/grid SVG used for answer bullets. Memoizing avoids recreating the same SVG on every render.
interface DotIconProps extends React.SVGProps<SVGSVGElement> {
    style?: React.CSSProperties;
    className?: string;
    draggable?: boolean;
    onDragStart?: React.DragEventHandler<SVGSVGElement>;
    onDragEnd?: React.DragEventHandler<SVGSVGElement>;
}

const DotIcon: FC<DotIconProps> = memo(function DotIcon({ style, className, ...rest }: DotIconProps) {
    return (
        <svg
            width="7"
            height="10"
            viewBox="0 0 7 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'grab', ...style }}
            className={className}
            aria-hidden
            focusable={false}
            {...rest}
        >
            <path d="M2 1.5C2 1.91421 1.66421 2.25 1.25 2.25C0.835786 2.25 0.5 1.91421 0.5 1.5C0.5 1.08579 0.835786 0.75 1.25 0.75C1.66421 0.75 2 1.08579 2 1.5Z" fill="currentColor" fillOpacity="1" />
            <path d="M6.5 1.5C6.5 1.91421 6.16421 2.25 5.75 2.25C5.33579 2.25 5 1.91421 5 1.5C5 1.08579 5.33579 0.75 5.75 0.75C6.16421 0.75 6.5 1.08579 6.5 1.5Z" fill="currentColor" fillOpacity="1" />
            <path d="M2 5C2 5.41421 1.66421 5.75 1.25 5.75C0.835786 5.75 0.5 5.41421 0.5 5C0.5 4.58579 0.835786 4.25 1.25 4.25C1.66421 4.25 2 4.58579 2 5Z" fill="currentColor" fillOpacity="1" />
            <path d="M6.5 5C6.5 5.41421 6.16421 5.75 5.75 5.75C5.33579 5.75 5 5.41421 5 5C5 4.58579 5.33579 4.25 5.75 4.25C6.16421 4.25 6.5 4.58579 6.5 5Z" fill="currentColor" fillOpacity="1" />
            <path d="M2 8.5C2 8.91421 1.66421 9.25 1.25 9.25C0.835786 9.25 0.5 8.91421 0.5 8.5C0.5 8.08579 0.835786 7.75 1.25 7.75C1.66421 7.75 2 8.08579 2 8.5Z" fill="currentColor" fillOpacity="1" />
            <path d="M6.5 8.5C6.5 8.91421 6.16421 9.25 5.75 9.25C5.33579 9.25 5 8.91421 5 8.5C5 8.08579 5.33579 7.75 5.75 7.75C6.16421 7.75 6.5 8.08579 6.5 8.5Z" fill="currentColor" fillOpacity="1" />
        </svg>
    );
});
DotIcon.displayName = 'DotIcon';

// Drag state for reordering answers inside a question
type DragState = {
    questionId: string;
    fromIndex: number;
    overIndex?: number;
} | null;

export interface SurveyFormProps {
    surveyId?: string | undefined;
    onClose?: () => void;
}


export const SurveyForm: FC<SurveyFormProps> = ({ surveyId, onClose }) => {
    const { t } = useTranslation();
    const { dispatchNotification } = useNotification();
    const { theme } = useTheme();
    const styles = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationAttemptRef = useRef<string | null>(null);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [questions, setQuestions] = useState<Question[]>([defaultQuestion(`q-${Date.now()}`)]);
    const [surveyTitle, setSurveyTitle] = useState<string>('');
    const [surveySubtitle, setSurveySubtitle] = useState<string>('');
    const [surveyDescription, setSurveyDescription] = useState<string>('');
    const [isTitleDuplicate, setIsTitleDuplicate] = useState<boolean>(false);
    const [allSurveys, setAllSurveys] = useState<SurveyHeader[]>([]);

    // Store initial state for dirty check
    const [initialState, setInitialState] = useState<{
        surveyTitle: string;
        surveySubtitle: string;
        surveyDescription: string;
        questions: Question[];
    } | null>(null);

    // Dirty state detection
    const isDirty = useMemo(() => {
        if (!initialState) return false;
        
        // Check simple fields first
        if (surveyTitle !== initialState.surveyTitle) return true;
        if (surveySubtitle !== initialState.surveySubtitle) return true;
        if (surveyDescription !== initialState.surveyDescription) return true;
        
        // Deep compare questions
        if (questions.length !== initialState.questions.length) return true;
        for (let i = 0; i < questions.length; i++) {
            const qa = questions[i], qb = initialState.questions[i];
            if (
                qa.id !== qb.id ||
                qa.label !== qb.label ||
                qa.type !== qb.type ||
                qa.required !== qb.required ||
                qa.maxLength !== qb.maxLength ||
                qa.maxAnswers !== qb.maxAnswers ||
                JSON.stringify(qa.answers ?? []) !== JSON.stringify(qb.answers ?? [])
            ) return true;
        }
        
        return false;
    }, [surveyTitle, surveySubtitle, surveyDescription, questions, initialState]);

    // Set initial state when survey loads (edit or new)
    useEffect(() => {
        if (surveyId) {
            // Wait for survey to load, handled in other useEffect
        } else {
            // New survey, set initial state to blank
            setInitialState({
                surveyTitle: '',
                surveySubtitle: '',
                surveyDescription: '',
                questions: [defaultQuestion(`q-${Date.now()}`)]
            });
        }
    }, [surveyId]);

    // Intercepte le bouton précédent du navigateur
    useEffect(() => {
        if (!isDirty) return;

        // Ajouter un état à l'historique pour détecter le back
        const currentState = window.history.state;
        window.history.pushState({ ...currentState, preventBack: true }, '');

        const handlePopState = () => {
            if (isDirty) {
                // Bloquer la navigation en ajoutant un nouvel état
                window.history.pushState({ ...currentState, preventBack: true }, '');
                setShowConfirmDialog(true);
                navigationAttemptRef.current = 'back';
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
            // Nettoyer l'état ajouté si le composant se démonte normalement
            if (window.history.state?.preventBack) {
                window.history.back();
            }
        };
    }, [isDirty]);

    // Intercepte fermeture/refresh onglet avec beforeunload de react-router
    useBeforeUnload(
        useCallback((e) => {
            if (isDirty) {
                e.preventDefault();
                return e.returnValue = '';
            }
        }, [isDirty])
    );

    // Intercepte le bouton close
    const handleCloseClick = (e?: any) => {
        if (e) e.preventDefault();
        if (isDirty) {
            setShowConfirmDialog(true);
        } else {
            if (onClose) onClose();
        }
    };
    const handleConfirm = () => {
        setShowConfirmDialog(false);
        if (navigationAttemptRef.current === 'back') {
            navigationAttemptRef.current = null;
            // Nettoyer l'état preventBack avant de naviguer
            if (window.history.state?.preventBack) {
                window.history.back();
            }
            window.history.back();
        } else if (onClose) {
            onClose();
        }
    };
    const handleCancel = () => {
        setShowConfirmDialog(false);
        navigationAttemptRef.current = null;
    };

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [responsesCount, setResponsesCount] = useState<number | null>(null);
    const [usersCount, setUsersCount] = useState<number | null>(null);
    const [countsLoading, setCountsLoading] = useState<boolean>(false);
    const [countsError, setCountsError] = useState<string | null>(null);

    // Load all surveys for duplicate checking
    useEffect(() => {
        const loadAllSurveys = async () => {
            try {
                const client = ServiceFactory.EnterpriseGPTApiClient() as unknown as EnterpriseGPTApiV1ClientWrapper;
                const surveys = await client.getAllSurveyHeaders();
                setAllSurveys(surveys || []);
            } catch (err) {
                console.error('Failed to load all surveys:', err);
            }
        };
        loadAllSurveys();
    }, []);

    // Check for duplicate title whenever surveyTitle changes
    useEffect(() => {
        if (!surveyTitle.trim()) {
            setIsTitleDuplicate(false);
            return;
        }
        const titleLower = surveyTitle.toLowerCase().trim();
        const isDuplicate = allSurveys.some(s => 
            s.title && s.title.toLowerCase().trim() === titleLower && s.id !== surveyId
        );
        setIsTitleDuplicate(isDuplicate);
    }, [surveyTitle, allSurveys, surveyId]);

    useEffect(() => {
        if (surveyId) {
            setLoading(true);
            ServiceFactory.EnterpriseGPTApiClient().getSurvey(surveyId)
                .then((survey: ISurvey) => {
                    if (survey) {
                        if (survey.title) setSurveyTitle(survey.title);
                        if (survey.subtitle) setSurveySubtitle(survey.subtitle);
                        if (survey.description) setSurveyDescription(survey.description);
                    }

                    // Map server questions (IApiQuestion[]) to local Question[] and populate state
                    let mapped: Question[] = [defaultQuestion(`q-${Date.now()}`)];
                    if (survey && survey.questions && survey.questions.length > 0) {
                        mapped = (survey.questions as IApiQuestion[]).map((sq, idx) => {
                            const qid = sq.id || `q-${Date.now()}-${idx}`;
                            const qtype: QuestionType = sq.type as QuestionType;
                            const answers = sq.answerOptions ? [...sq.answerOptions] : undefined;
                            const maxLength = sq.maxLength;
                            const maxAnswers = sq.maxAnswers;
                            return {
                                id: qid,
                                label: sq.text || '',
                                type: qtype,
                                required: !!sq.isMandatory,
                                answers,
                                maxLength,
                                maxAnswers,
                            } as Question;
                        });
                        setQuestions(mapped.length ? mapped : [defaultQuestion(`q-${Date.now()}`)]);
                    }
                    // Set initial state for dirty check after loading survey
                    setInitialState({
                        surveyTitle: survey?.title ?? '',
                        surveySubtitle: survey?.subtitle ?? '',
                        surveyDescription: survey?.description ?? '',
                        questions: mapped
                    });
                })
                .catch((err) => console.error('Failed to load survey', err))
                .finally(() => setLoading(false));
        }
    }, [surveyId]);

    // Fetch responses/users counts
    useEffect(() => {
        let cancelled = false;
        const client = ServiceFactory.EnterpriseGPTApiClient() as unknown as EnterpriseGPTApiV1ClientWrapper;
        async function loadCounts() {
            setCountsLoading(true);
            setCountsError(null);
            try {
                // Get responses for this survey (only if surveyId provided)
                if (surveyId) {
                    try {
                        const resp = await client.getNoOfReponsesCount(surveyId);
                        // eslint-disable-next-line no-console
                        console.debug('[SurveyForm] getNoOfReponsesCount raw:', resp);
                        if (!cancelled) setResponsesCount(resp ?? null);
                    } catch (e) {
                        // fallback to null and continue
                        // eslint-disable-next-line no-console
                        console.log('[SurveyForm] getNoOfReponses error:', e);
                        if (!cancelled) setResponsesCount(null);
                    }
                } else {
                    if (!cancelled) setResponsesCount(null);
                }

                // Get total users
                try {
                    const uresp = await client.getNoOfUsersCount();
                    // DEBUG: log raw response
                    // eslint-disable-next-line no-console
                    console.debug('[SurveyForm] getNoOfUsersCount raw:', uresp);
                    if (!cancelled) setUsersCount(uresp ?? null);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.debug('[SurveyForm] getNoOfUsersCount error:', e);
                    if (!cancelled) setUsersCount(null);
                }
            } catch (err: unknown) {
                if (!cancelled) {
                    let msg = 'Failed to load counts';
                    if (typeof err === 'string') msg = err;
                    else if (err && typeof err === 'object') {
                        const rec = err as Record<string, unknown>;
                        if ('message' in rec && typeof rec.message === 'string') msg = rec.message as string;
                    }
                    setCountsError(msg);
                }
            } finally {
                if (!cancelled) setCountsLoading(false);
            }
        }

        loadCounts();
        return () => { cancelled = true; };
    }, [surveyId]);



    // Helper to extract numeric counts — server returns an integer (or numeric string)
    const extractNumberFromResponse = (resp: unknown): number | null => {
        if (resp === null || resp === undefined) return null;
        if (typeof resp === 'number') return resp;
        if (typeof resp === 'string') {
            const parsed = parseInt(resp, 10);
            return Number.isFinite(parsed) ? parsed : null;
        }
        try {
            const s = String(resp);
            const p = parseInt(s, 10);
            return Number.isFinite(p) ? p : null;
        } catch {
            return null;
        }
    };


    const insertQuestion = useCallback((index?: number) => {
        setQuestions((q) => {
            const newQ = defaultQuestion(`q-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
            if (typeof index === 'number' && index >= 0 && index <= q.length) {
                const copy = [...q];
                copy.splice(index, 0, newQ);
                return copy;
            }
            return [...q, newQ];
        });
    }, []);

    const [collapsedQuestions, setCollapsedQuestions] = useState<Set<string>>(new Set());

    const toggleQuestion = (id: string) => {
        setCollapsedQuestions(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleExpandAll = () => {
        setCollapsedQuestions(new Set());
    };

    const handleCollapseAll = () => {
        const allIds = new Set(questions.map(q => q.id));
        setCollapsedQuestions(allIds);
    };

    const addQuestion = useCallback(() => {
        setQuestions((q) => [...q, defaultQuestion(`q-${Date.now()}-${Math.floor(Math.random() * 1000)}`)]);
    }, []);

    const removeQuestion = useCallback((id: string) => {
        setQuestions((q) => q.filter(x => x.id !== id));
    }, []);

    const updateQuestion = useCallback((id: string, patch: Partial<Question>) => {
        setQuestions((q) => q.map(x => x.id === id ? { ...x, ...patch } : x));
    }, []);

    const moveQuestion = useCallback((id: string, direction: 'up' | 'down') => {
        setQuestions(prev => {
            const index = prev.findIndex(p => p.id === id);
            if (index === -1) return prev;
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= prev.length) return prev;
            const copy = [...prev];
            [copy[index], copy[newIndex]] = [copy[newIndex], copy[index]];
            return copy.map((q, i) => ({ ...q, id: `q${i + 1}` }));
        });
    }, []);

    const addAnswer = useCallback((id: string) => {
        setQuestions((q) => q.map(x => x.id === id ? { ...x, answers: [...(x.answers || []), ''] } : x));
    }, []);

    const updateAnswer = useCallback((id: string, index: number, value: string) => {
        setQuestions((q) => q.map(x => x.id === id ? { ...x, answers: (x.answers || []).map((a, i) => i === index ? value : a) } : x));
    }, []);

    const removeAnswer = useCallback((id: string, index: number) => {
        setQuestions((q) => q.map(x => x.id === id ? { ...x, answers: (x.answers || []).filter((_, i) => i !== index) } : x));
    }, []);

    const questionCount = useMemo(() => questions.length, [questions]);

    // Validation: track if any question has an empty label
    const hasEmptyLabel = useMemo(() => questions.some(q => !q.label || q.label.trim() === ''), [questions]);
    const firstEmptyQuestionId = useMemo(() => {
        const q = questions.find(q => !q.label || q.label.trim() === '');
        return q ? q.id : undefined;
    }, [questions]);

    // Helper to pick a background color for NPS values (0-10)
    const getNpsColor = useCallback((n: number) => {
        // 0-6: red shades, 7-8: amber, 9-10: green shades
        if (n <= 6) return '#FEE2E2';
        if (n <= 8) return '#FFEDD5';
        return '#DCFCE7';
    }, []);

    // Drag & drop state for answers
    const [dragState, setDragState] = useState<DragState>(null);

    const onAnswerDragStart = useCallback((questionId: string, index: number, e: React.DragEvent) => {
        // store the origin in dataTransfer for accessibility and set local drag state
        e.dataTransfer.setData('text/plain', JSON.stringify({ questionId, index }));
        e.dataTransfer.effectAllowed = 'move';
        setDragState({ questionId, fromIndex: index, overIndex: index });
    }, []);

    const onAnswerDragOver = useCallback((questionId: string, overIndex: number, e: React.DragEvent) => {
        e.preventDefault(); // allow drop
        // set visual drop effect and update state
        try {
            e.dataTransfer.dropEffect = 'move';
        } catch (err) {
            // ignore if not supported
        }
        setDragState(prev => {
            if (!prev) return prev;
            if (prev.questionId !== questionId) return prev;
            if (prev.overIndex === overIndex) return prev;
            return { ...prev, overIndex };
        });
    }, []);

    const onAnswerDragLeave = useCallback((questionId: string, e: React.DragEvent) => {
        // when leaving a drop zone, clear overIndex if it belongs to the same question
        try { e.preventDefault(); } catch { };
        setDragState(prev => {
            if (!prev) return prev;
            if (prev.questionId !== questionId) return prev;
            return { ...prev, overIndex: undefined };
        });
    }, []);

    const reorderAnswers = useCallback((questionId: string, from: number, to: number) => {
        setQuestions((prev) => prev.map(q => {
            if (q.id !== questionId) return q;
            const answers = [...(q.answers || [])];
            if (from < 0 || from >= answers.length) return q;
            // allow to be answers.length (append)
            if (to < 0 || to > answers.length) return q;
            const [item] = answers.splice(from, 1);
            // When removing an earlier item, the indexes shift left by 1
            const insertIndex = from < to ? to - 1 : to;
            answers.splice(insertIndex, 0, item);
            return { ...q, answers };
        }));
    }, []);

    // (drag & drop handles reordering now)

    const onAnswerDrop = useCallback((questionId: string, toIndex: number, e: React.DragEvent) => {
        e.preventDefault();
        let handled = false;
        try {
            const payload = e.dataTransfer.getData('text/plain');
            const parsed = JSON.parse(payload);
            if (parsed && parsed.questionId === questionId && typeof parsed.index === 'number') {
                reorderAnswers(questionId, parsed.index, toIndex);
                handled = true;
            }
        } catch (err) {
            // ignore and fallback
        }
        if (!handled) {
            // fallback to local dragState
            setDragState(prev => {
                if (prev && prev.questionId === questionId && typeof prev.fromIndex === 'number') {
                    reorderAnswers(questionId, prev.fromIndex, toIndex);
                }
                return null;
            });
        } else {
            setDragState(null);
        }
    }, [reorderAnswers]);

    const onAnswerDragEnd = useCallback(() => {
        setDragState(null);
    }, []);

    const saveSurvey = useCallback(async () => {
        // Client-side validation: ensure all question labels are present
        if (hasEmptyLabel) {
            // Focus/scroll to first invalid input if possible and show notification
            try {
                const el = document.getElementById(`q-label-${firstEmptyQuestionId}`) as HTMLInputElement | null;
                if (el) {
                    el.focus();
                }
            } catch { }
            try {
                dispatchNotification(t('adminSettings.surveys.validation.missingLabel', 'Each question must have a label.'), 'error');
            } catch { }
            return;
        }

        // Validate title uniqueness before saving
        if (!surveyTitle.trim()) {
            dispatchNotification(t('adminSettings.surveys.validation.missingTitle', 'Survey name is required.'), 'error');
            return;
        }

        // Check for duplicate title (already tracked by state)
        if (isTitleDuplicate) {
            // Focus on the title input
            try {
                const el = document.getElementById('survey-title') as HTMLInputElement | null;
                if (el) el.focus();
            } catch { }
            return;
        }

        setSaving(true);

        let saved = false;
        try {
            // Map local Question[] back to API Survey shape
            const apiQuestions: IApiQuestion[] = questions.map((q) => ({
                id: q.id,
                text: q.label,
                type: q.type as SurveyAnswerType,
                isMandatory: q.required,
                answerOptions: q.answers,
                maxLength: q.maxLength,
                maxAnswers: q.maxAnswers,
            } as IApiQuestion));

            const client = ServiceFactory.EnterpriseGPTApiClient();

            // If we have an existing surveyId, fetch its header and merge header fields to avoid losing metadata
            let headerData: SurveyHeader | undefined;
            if (surveyId) {
                try {
                    headerData = await client.getSurveyHeader(surveyId);
                } catch (err) {
                    console.warn('Could not retrieve survey header before save, proceeding without it', err);
                }
            }

            const payloadObj: Partial<ISurvey> = {
                id: surveyId,
                title: surveyTitle,
                subtitle: surveySubtitle,
                description: surveyDescription,
                questions: apiQuestions,
            };

            if (headerData) {
                // Merge known header fields if present (editing existing survey)
                if (headerData.version) payloadObj.version = headerData.version;
                if (headerData.subtitle) payloadObj.subtitle = headerData.subtitle;
                if (headerData.description) payloadObj.description = headerData.description;
                if (headerData.status) payloadObj.status = headerData.status;
                if (headerData.creationDate) payloadObj.creationDate = headerData.creationDate;
                if (headerData.lastChangeDate) payloadObj.lastChangeDate = headerData.lastChangeDate;
                if (headerData.activationDate) payloadObj.activationDate = headerData.activationDate;
                if (headerData.closedDate) payloadObj.closedDate = headerData.closedDate;
            } else {
                // Creating a new survey - set status to Draft
                payloadObj.status = SurveyStatus.Draft;
            }

            const surveyPayload = Survey.fromJS(payloadObj);
            await client.upsertSurvey(surveyPayload);
            
            // Show success notification
            const isNewSurvey = !surveyId;
            const successMessage = isNewSurvey 
                ? t('adminSettings.surveys.createSuccess', 'Survey created successfully')
                : t('adminSettings.surveys.editSuccess', 'Survey edited successfully');
            dispatchNotification(successMessage, 'success');
            
            saved = true;
        } catch (err) {
            console.error('Failed to save survey', err);
            try {
                // Show user-facing error notification
                dispatchNotification(t('adminSettings.surveys.saveError', 'Failed to save survey. Please try again.'), 'error');
            } catch (e) {
                // noop
            }
        } finally {
            setSaving(false);
            // Only close the form after a successful save
            if (saved && onClose) {
                // Reset initial state to avoid dirty flag after save
                setInitialState({
                    surveyTitle,
                    surveySubtitle,
                    surveyDescription,
                    questions: [...questions]
                });
                try {
                    onClose();
                } catch (err) {
                    // swallow errors from parent close handlers
                }
            }
        }
    }, [questions, surveyId, surveyTitle, surveySubtitle, surveyDescription, onClose, dispatchNotification, t, hasEmptyLabel, firstEmptyQuestionId]);

    return (
        <div className={styles.page} data-name="SurveyFormPage">

            <div className="titleSection">
                <div className={mergeClasses("titleSection-content", "titleSection-content-indented", styles.titleContainer)}>
                    <Text className={mergeClasses("title", styles.titleText)}>
                        {loading ? t('loading', 'Loading...') : (surveyTitle || 'New Survey')}
                    </Text>
                    {/* Combined badge placed next to the title */}

                </div>

                <div className="titleSection-actions">
                    <SyButton appearance="primary" onClick={saveSurvey} disabled={loading || saving || hasEmptyLabel} style={{ minWidth: 120 }} icon={<Save24Regular />}>{saving ? t('saving', 'Saving...') : surveyId ? t('saveChanges', 'Save') : t('create', 'Create')}</SyButton>
                    <SyButton appearance="transparent" onClick={handleCloseClick} icon={<Dismiss24Regular />} aria-label="Close" title="Close" />
                </div>
            </div>

            <HorizontalSeparatorLine />

            <div className={styles.body} >
                {/* Survey Details Section */}
                <div className={`${styles.cardSurveyDetails} survey-form-card`}>
                    <div className={styles.surveyDetailsContent}>
                        <Text weight="semibold" size={400}>Survey Details</Text>

                        <div className={styles.surveyDetailsRow}>
                            <div className={styles.surveyDetailsField}>
                                <label className={styles.label} htmlFor="survey-title">Survey Name<span className={styles.requiredAsterisk}>*</span></label>
                                <Input
                                    id="survey-title"
                                    value={surveyTitle}
                                    onChange={(_, data) => setSurveyTitle(data.value)}
                                    placeholder="Enter survey name"
                                    className={mergeClasses(styles.fullWidthInput, styles.customInput)}
                                    style={isTitleDuplicate ? { borderColor: 'var(--color-red-500)', outlineColor: 'var(--color-red-500)' } : {}}
                                />
                                {isTitleDuplicate && (
                                    <div className={styles.validationError}>
                                        {t('adminSettings.surveys.titleExists', 'A survey with this name already exists')}
                                    </div>
                                )}
                            </div>
                            <div className={styles.surveyDetailsField}>
                                <label className={styles.label} htmlFor="survey-subtitle">Subtitle</label>
                                <Input
                                    id="survey-subtitle"
                                    value={surveySubtitle}
                                    onChange={(_, data) => setSurveySubtitle(data.value)}
                                    placeholder="Enter subtitle"
                                    className={mergeClasses(styles.fullWidthInput, styles.customInput)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={styles.label} htmlFor="survey-description">Description</label>
                            <Textarea
                                id="survey-description"
                                value={surveyDescription}
                                onChange={(_, data) => setSurveyDescription(data.value)}
                                placeholder="Enter description"
                                className={mergeClasses(styles.fullWidthInput, styles.customTextarea)}
                                resize="vertical"
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.contentInner}>
                    {/* Expand/Collapse All Control Card */}
                    {questions.length > 0 && (
                        <div className={`${styles.controlCard} survey-form-card`}>
                            <SyButton
                                appearance="transparent"
                                icon={<ChevronsDown />}
                                onClick={handleExpandAll}
                                className={styles.smallButton}
                            >
                                {t('expandAll', 'Expand all')}
                            </SyButton>
                            <SyButton
                                appearance="transparent"
                                icon={<ChevronsUp />}
                                onClick={handleCollapseAll}
                                className={styles.smallButton}
                            >
                                {t('collapseAll', 'Collapse all')}
                            </SyButton>
                        </div>
                    )}

                    {questions.map((q, idx) => (
                        <Fragment key={q.id}>
                            <div className={`${styles.card} survey-form-card`}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <div className="survey-form-question-number">Question n°{idx + 1}</div>
                                        <SyButton
                                            appearance="transparent"
                                            icon={collapsedQuestions.has(q.id) ? <ChevronDown /> : <ChevronUp />}
                                            onClick={() => toggleQuestion(q.id)}
                                            aria-label={collapsedQuestions.has(q.id) ? "Expand" : "Collapse"}
                                        />
                                    </div>
                                    <div className="survey-form-question-content">
                                        <div className="survey-form-question-label-wrapper">
                                            <label className={styles.label} htmlFor={`q-label-${q.id}`}>Question<span className={styles.requiredAsterisk}>*</span></label>
                                            <Input id={`q-label-${q.id}`} placeholder="Add a question label" value={q.label} onChange={(_, data) => updateQuestion(q.id, { label: data.value })} className={mergeClasses(styles.questionInput, styles.customInput, styles.fullWidthInput)} aria-invalid={q.label.trim() === ''} size="large" />
                                            {q.label.trim() === '' && (
                                                <Text role="alert" className={styles.validationError}>{t('adminSettings.surveys.validation.questionLabelRequired', 'Question label is required')}</Text>
                                            )}
                                        </div>

                                        <div style={{ minWidth: 200, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                                            <div style={{ minWidth: 140 }}>
                                                <label className={styles.label} htmlFor={`q-type-${q.id}`}>{t('survey.questionType.label', 'Type of question')}</label>
                                                <Select id={`q-type-${q.id}`} value={q.type} onChange={(_, data) => updateQuestion(q.id, { type: data.value as QuestionType, answers: data.value === 'Text' ? undefined : (q.answers ?? ['']) })}>
                                                    <option value="Rating">{t('survey.questionType.options.rating', 'Star rating')}</option>
                                                    <option value="SingleValue">{t('survey.questionType.options.singleValue', 'Single choice')}</option>
                                                    <option value="MultipleValue">{t('survey.questionType.options.multipleValue', 'Multiple choice')}</option>
                                                    <option value="Text">{t('survey.questionType.options.text', 'Open question')}</option>
                                                    <option value="NPSScale">{t('survey.questionType.options.nps', 'NPS scale')}</option>
                                                </Select>
                                            </div>
                                            <SyButton appearance="transparent" onClick={() => removeQuestion(q.id)} aria-label="Delete question" title="Delete question"
                                                icon={<Trash2 size={16} />} overrideForegroundColorLight="var(--color-red-500)" style={{ padding: '0px', minWidth: 0, marginTop: '20px' }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {!collapsedQuestions.has(q.id) && (
                                    <>
                                        {/* Required toggle placed above the type-specific section */}
                                        <div style={{ marginTop: 12, marginBottom: 12 }}>
                                            <Switch checked={q.required} onChange={(_, data) => updateQuestion(q.id, { required: !!data.checked })} className="survey-form-required-switch" />
                                            <Text>Required question</Text>
                                        </div>

                                        <Divider />

                                        {/* Type-specific area */}

                                        {q.type === 'SingleValue' || q.type === 'MultipleValue' ? (
                                            <div className={`${styles.answersContainer} survey-form-answers-container`}>
                                                <Text weight="semibold" className="survey-form-answers-title">Answers</Text>
                                                <div className={styles.answersList}>
                                                    {/* Render a drop zone before each answer and one at the end. Use index positions 0..answers.length as drop targets */}
                                                    {((q.answers || []) || []).map((a, i) => {
                                                        const dropIndex = i; // drop before item i
                                                        const isDropActive = !!(dragState && dragState.questionId === q.id && dragState.overIndex === dropIndex);
                                                        return (
                                                            <div key={`block-${i}`}>
                                                                {/* Drop zone before item */}
                                                                <div
                                                                    onDragEnter={(e) => { e.preventDefault(); onAnswerDragOver(q.id, dropIndex, e); }}
                                                                    onDragOver={(e) => onAnswerDragOver(q.id, dropIndex, e)}
                                                                    onDragLeave={(e) => onAnswerDragLeave(q.id, e)}
                                                                    onDrop={(e) => onAnswerDrop(q.id, dropIndex, e)}
                                                                    style={{ height: 6, background: isDropActive ? 'rgba(0,0,0,0.06)' : 'transparent', margin: '2px 0', borderRadius: 2 }}
                                                                />

                                                                {/* Answer row */}
                                                                <div
                                                                    key={i}
                                                                    className="survey-form-answer-row"
                                                                >
                                                                    <Input value={a} onChange={(_, data) => updateAnswer(q.id, i, data.value)} placeholder={`Option ${i + 1}`} style={{ flex: 1 }} className={mergeClasses(styles.answerInput, styles.customInput)} />
                                                                    <SyButton
                                                                        appearance="transparent"
                                                                        onClick={() => removeAnswer(q.id, i)}
                                                                        aria-label="Remove answer"
                                                                        title="Remove answer"
                                                                        style={{ marginLeft: '-2px', padding: '0px', minWidth: 0 }}
                                                                    >
                                                                        <LucideX size={14} color={tokens.colorNeutralForeground3} />
                                                                    </SyButton>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}

                                                    {/* Drop zone at the end (append) */}
                                                    <div
                                                        onDragEnter={(e) => { e.preventDefault(); onAnswerDragOver(q.id, (q.answers || []).length, e); }}
                                                        onDragOver={(e) => onAnswerDragOver(q.id, (q.answers || []).length, e)}
                                                        onDragLeave={(e) => onAnswerDragLeave(q.id, e)}
                                                        onDrop={(e) => onAnswerDrop(q.id, (q.answers || []).length, e)}
                                                        style={{ height: 6, background: !!(dragState && dragState.questionId === q.id && dragState.overIndex === (q.answers || []).length) ? 'rgba(0,0,0,0.06)' : 'transparent', margin: '2px 0', borderRadius: 2 }}
                                                    />
                                                </div>
                                                <div className={styles.addAnswerBtn}>
                                                    <SyButton appearance="quarterly" icon={<Add24Regular />} onClick={() => addAnswer(q.id)}>New answer</SyButton>
                                                </div>
                                            </div>
                                        ) : q.type === 'Rating' ? (
                                            <div style={{ marginTop: 8 }}>
                                                {/* Values preview for Star Rating (no Answers inputs) */}
                                                <Text weight="semibold" style={{ marginTop: 12, marginBottom: 12 }}>Values</Text>
                                                <div className={styles.starsRow}>
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <img key={i} src={STAR_ICON_PATH} alt="star" width={28} height={26} />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : q.type === 'Text' ? (
                                            <div style={{ marginTop: 8 }}>
                                                <label className={styles.label}>Maximum character length (optional)</label>
                                                <Input type="number" placeholder="e.g., 500" value={q.maxLength?.toString() ?? ''} onChange={(_, data) => updateQuestion(q.id, { maxLength: data.value ? parseInt(data.value) : undefined })} className={styles.customInput} />
                                            </div>
                                        ) : q.type === 'NPSScale' ? (
                                            <div style={{ marginTop: 8 }}>
                                                {/* Separator line smaller than the main divider to visually separate the values area */}
                                                <div style={{ marginTop: 8, marginBottom: 8, height: 1, background: 'var(--color-neutral-200)' }} />
                                                <Text weight="semibold" style={{ marginBottom: 8 }}>Values</Text>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                                    {Array.from({ length: 11 }).map((_, i) => (
                                                        <div key={`nps-${i}`} style={{
                                                            width: 40,
                                                            height: 40,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: 6,
                                                            background: getNpsColor(i),
                                                            color: tokens.colorNeutralForeground1,
                                                            fontWeight: 600,
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                                        }}>{i}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        {q.type === 'MultipleValue' && (
                                            <div style={{ marginTop: 8 }}>
                                                <label className={styles.label}>Maximum number of answers (optional)</label>
                                                <Input type="number" placeholder="e.g., 3" value={q.maxAnswers?.toString() ?? ''} onChange={(_, data) => updateQuestion(q.id, { maxAnswers: data.value ? parseInt(data.value) : undefined })} className={styles.customInput} />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* render separator between every two blocks */}
                            {idx < questions.length - 1 && (
                                <div className={styles.separator}>
                                    <div className={styles.separatorLine} />
                                    <div className={styles.separatorDot}>
                                        <div className={styles.separatorButton}>
                                            {/* Use global primary color variable for the plus icon/button via SyButton props */}
                                            <SyButton appearance="transparent" overrideForegroundColorLight="var(--color-primary-500)" overrideForegroundColorDark="var(--color-primary-400)" icon={<PlusCircle />} onClick={() => insertQuestion(idx + 1)}></SyButton>
                                        </div>
                                    </div>
                                    <div className={styles.separatorLine} />
                                </div>
                            )}
                        </Fragment>
                    ))}
                </div>

                <div className={styles.addQuestionBar}>
                    <SyButton appearance="outline" icon={<PlusCircle size={16} />} onClick={addQuestion}>New question</SyButton>
                </div>
            </div>

            <CDialog
                open={showConfirmDialog}
                title={t('adminSettings.survey.leavePage.title')}
                primaryActionText={t('adminSettings.survey.leavePage.confirmButton')}
                secondaryActionText={t('adminSettings.survey.leavePage.cancelButton')}
                onPrimaryAction={handleConfirm}
                onSecondaryAction={handleCancel}
            >
                <div>{t('adminSettings.survey.leavePage.message')}</div>
            </CDialog>
        </div >
    );
};

export default SurveyForm;
